import { useState, useEffect, useCallback } from "react";

import { Breadcrumb, Button, Input } from "antd";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";

import { ActiveIdentityCard } from "./ActiveIdentityCard";
import { ClaimCard } from "./ClaimCard";
import { RemovedIdentityCard } from "./RemovedIdentityCard";
import UserService from "../services/UserService";
import { SECONDARY_WALLET_ENABLED } from "../utils/featureFlags";

function toTitleCase(str) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

// Maps an audited status to the label shown next to the acting admin.
function statusActionLabel(status) {
  switch (status) {
    case "Denied":
      return "Denied by";
    case "Under Review":
      return "Moved to Under Review by";
    case "Tester":
      return "Moved to Tester by";
    case "Suspended":
      return "Suspended by";
    case "Unsuspended":
      return "Unsuspended by";
    default:
      return `Moved to ${status} by`;
  }
}

// Shared card-entry markup for both the Suspension History and Status Change
// History sections — same shape, different `entries`.
function renderHistoryEntry(entry) {
  return (
    <div key={entry.objectId} className="border rounded-xl p-4 bg-white">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="rounded-full border px-3 py-1 text-xs font-bold border-[#9952b3] text-[#9952b3]">{entry.toStatus}</span>
        {entry.fromStatus && <span className="text-xs text-gray-400">from {entry.fromStatus}</span>}
      </div>
      <div className="mt-2 text-sm text-gray-700 break-all">
        <span className="font-semibold">{statusActionLabel(entry.toStatus)}:</span> {entry.complianceUserEmail || "system"}
        {entry.complianceUserWallet && <span className="text-gray-500"> ({entry.complianceUserWallet})</span>}
        {entry.isTrustedIssuer && (
          <span className="ml-2 rounded-full border px-2 py-0.5 text-xs font-bold border-green-500 text-green-500">Trusted Issuer</span>
        )}
      </div>
      <div className="text-sm text-gray-500">
        <span className="font-semibold">On:</span> {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : "—"}
      </div>
    </div>
  );
}

function DigitalIdentityDetailView({ service }) {
  const { identityId, userId } = useParams();
  const [displayName, setDisplayName] = useState("");
  const [identity, setIdentity] = useState({});
  const personaData = identity?.personaData;
  const [secondaryWallets, setSecondaryWallets] = useState([]);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [statusHistory, setStatusHistory] = useState([]);
  const [resetHistory, setResetHistory] = useState([]);
  const [removedIdentity, setRemovedIdentity] = useState(null);

  // Determine identity type based on the templateId
  const templateId = personaData?.payload?.included?.filter((item) => item.type === "inquiry-template").map((item) => item.id)[0];

  const identityType =
    templateId === process.env.REACT_APP_PERSONA_KYC_TEMPLATEID ? "KYC" : templateId === process.env.REACT_APP_PERSONA_KYB_TEMPLATEID ? "KYB" : "";

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const userEmail = searchParams.get("email") || null;
  // Originating Identities tab (passed as ?from= by IdentitiesPage). Drives the
  // breadcrumb link, back button and last crumb so navigation returns to the
  // tab the user came from instead of the default "Approved" tab.
  const fromTab = searchParams.get("from") || "";
  const identitiesPath = fromTab ? `/identities?tab=${encodeURIComponent(fromTab)}` : "/identities";
  const fromTabLabel = { Identities: "Approved", Claims: "Add Rules" }[fromTab] || fromTab || "Pending";
  // Record handed over from the Identities list via router state (see
  // IdentitiesPage ViewPendingIdentity). Used as the primary data source.
  const passedRecord = location.state?.record;

  // Split the already-fetched statusHistory client-side — no new network
  // request — so Suspended/Unsuspended entries get their own card instead of
  // mixing with Denied/Under Review/Tester in Status Change History.
  const suspensionHistory = statusHistory.filter((entry) => entry.toStatus === "Suspended" || entry.toStatus === "Unsuspended");
  const otherStatusHistory = statusHistory.filter((entry) => entry.toStatus !== "Suspended" && entry.toStatus !== "Unsuspended");

  // Process verifications
  // Process verifications with specific labels
  const verifications = personaData?.payload?.included
    ?.filter((item) => item.type?.startsWith("verification")) // Safeguard: Ensure item.type exists
    .map((item, index) => {
      // Define labels for KYC and KYB
      const kycLabels = [
        null, // First entry remains as-is
        "Proof of Address Verification", // Second entry gets this label
        "Selfie Verification", // Third entry gets this label
        "Identity Verification", // Fourth entry gets this label
      ];

      const kybLabels = [
        "Business Address Verification", // First entry for KYB
        "Bank Verification", // Second entry
        "EIN Verification", // Third entry
        "Incorporation Verification", // Fourth entry
        "Optional Verification 1", // Fifth entry
        "Optional Verification 2", // Sixth entry
      ];

      // Safeguard: Default label if item.type is undefined
      const defaultLabel = "Unknown Verification";

      // Select labels based on identityType and index
      let label;
      if (identityType === "KYC") {
        label = kycLabels[index] || (item.type ? toTitleCase(item.type.split("/")[1].replace("-", " ")) : defaultLabel);
      } else if (identityType === "KYB") {
        label = kybLabels[index] || (item.type ? toTitleCase(item.type.split("/")[1].replace("-", " ")) : defaultLabel);
      } else {
        label = item.type ? toTitleCase(item.type.split("/")[1].replace("-", " ")) : defaultLabel;
      }

      return {
        id: item.id,
        kind: label,
        status: item.attributes?.status?.toUpperCase() || "UNKNOWN", // Handle undefined status
      };
    });

  // Process documents with labels for KYC and KYB
  const documents = personaData?.payload?.included
    ?.filter((item) => {
      return item.type.startsWith("document");
    })
    .map((item, index) => {
      const kycLabels = ["Government Id", "Proof of Address"];
      const kybLabels = [
        "Proof of Business Address",
        "Bank Statement",
        "EIN Document",
        "Articles of Incorporation",
        "Optional Document 1",
        "Optional Document 2",
      ];

      // Assign labels based on identity type
      let label;
      if (identityType === "KYC") {
        label = kycLabels[index] || toTitleCase(item.type.split("/")[1].replace("-", " "));
      } else if (identityType === "KYB") {
        label = kybLabels[index] || toTitleCase(item.type.split("/")[1].replace("-", " "));
      } else {
        label = toTitleCase(item.type.split("/")[1].replace("-", " "));
      }

      return {
        id: item.id,
        kind: label,
        status: item.attributes?.status.toUpperCase(),
      };
    });

  const navigate = useNavigate();

  const getIdentity = useCallback(async () => {
    let result;
    if (identityId === "pending" && userId) {
      // Prefer the record passed via router state from the list. It already
      // holds the full attributes for any status, so we avoid the narrower
      // client-side _User query (pendingApproval=true && denied=false) that
      // misses users in other statuses and bounces back to /identities.
      let user = passedRecord && passedRecord.id === userId ? passedRecord : null;
      if (!user) {
        if (!service.getPendingIdentities) return;
        const pendingUsers = (await service.getPendingIdentities()) || [];
        user = pendingUsers.filter((item) => item.id === userId)[0];
      }
      if (!user) {
        navigate(identitiesPath);
        return;
      }
      const attrs = user.attributes || {};
      result = {
        displayName: `${attrs.firstName || ""} ${attrs.lastName || ""}`.trim(),
        walletAddress: attrs.walletAddress,
        accountNumber: attrs.personaReferenceId,
        personaData: attrs.personaVerificationData ? JSON.parse(attrs.personaVerificationData ?? "")?.data?.attributes : null,
        watchlistMatched: attrs.watchlistMatched,
        pepMatched: attrs.pepMatched,
      };
      // The Removed tab opens identities via this pending route (ViewPendingIdentity),
      // so resolve the removal details here. getRemovedIdentityDetails returns null for
      // non-removed users (and active identities), so the card only renders for removals.
      if (service.getRemovedIdentityDetails) {
        try {
          const removed = await service.getRemovedIdentityDetails(userId);
          setRemovedIdentity(removed || null);
        } catch (e) {
          console.error("getRemovedIdentityDetails failed:", e);
          setRemovedIdentity(null);
        }
      } else {
        setRemovedIdentity(null);
      }
    } else {
      if (!service.getDigitalIdentity) return;
      result = await service.getDigitalIdentity(identityId);
      // Fallback for any non-pending route that still resolves to a _User id.
      if (!result && service.getRemovedIdentityDetails) {
        const removed = await service.getRemovedIdentityDetails(identityId);
        setRemovedIdentity(removed || null);
      } else {
        setRemovedIdentity(null);
      }
    }
    setIdentity(result);
  }, [service, identityId, userId, passedRecord, navigate, identitiesPath]);

  const backBtn = () => {
    navigate(identitiesPath);
  };

  useEffect(() => {
    getIdentity().catch((error) => console.error("Error fetching identity:", error));
  }, [getIdentity]);

  useEffect(() => {
    if (SECONDARY_WALLET_ENABLED && userEmail) {
      void fetchUserWallets();
    }
    // fetchUserWallets is redefined every render (not memoized); only re-run this on userEmail change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail]);

  const fetchUserWallets = async () => {
    if (!userEmail) return;
    setLoadingWallets(true);
    try {
      const wallets = await UserService.getUserWallets(userEmail);
      setSecondaryWallets(wallets || []);
    } catch (error) {
      console.error("Error fetching wallets:", error);
    } finally {
      setLoadingWallets(false);
    }
  };

  // Load the status-change audit history and the Persona-reset history once we
  // have an identifier. Each backend query naturally returns empty for users
  // with no matching transitions, so the sections only render when populated.
  useEffect(() => {
    const auditUserId = userId || null;
    const auditWallet = identity?.walletAddress || null;
    if (!auditUserId && !auditWallet) return;
    void fetchStatusHistory(auditUserId, auditWallet);
    void fetchResetHistory(auditUserId, auditWallet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, identity?.walletAddress]);

  const fetchStatusHistory = async (auditUserId, auditWallet) => {
    try {
      const params = auditUserId ? { userId: auditUserId } : { walletAddress: auditWallet };
      const result = await UserService.getUserStatusHistory(params);
      setStatusHistory(result?.data || []);
    } catch (error) {
      console.error("Error fetching status history:", error);
      setStatusHistory([]);
    }
  };

  const fetchResetHistory = async (auditUserId, auditWallet) => {
    try {
      const params = auditUserId ? { userId: auditUserId } : { walletAddress: auditWallet };
      const result = await UserService.getPersonaResetHistory(params);
      setResetHistory(result?.data || []);
    } catch (error) {
      console.error("Error fetching persona reset history:", error);
      setResetHistory([]);
    }
  };

  return (
    <div>
      <Breadcrumb
        className="bg-transparent"
        items={[
          {
            title: <Link to={"/"}>Home</Link>,
          },
          {
            title: <Link to={identitiesPath}>Identities</Link>,
          },
          {
            title: identityId === "pending" ? fromTabLabel : identity?.displayName,
          },
        ]}
      />
      <p className="text-xl p-6">Identity Details</p>
      <hr></hr>
      <div className="p-6 max-[500px]:px-4 flex flex-col gap-4">
        <div>
          <label htmlFor="identityName">Identity display name *</label>
          <div className="mt-3 relative w-full flex">
            <Input
              id="identityName"
              value={identity?.displayName || ""}
              className="w-full p-2 text-xl"
              placeholder="Enter Display Name"
              type="text"
              maxLength={32}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <p className="absolute right-5 top-3">{displayName.length}/32</p>
          </div>
        </div>
        <div className="border rounded-xl p-6 bg-white flex flex-col gap-3 ">
          <label htmlFor="investorWalletAddress">Investor Wallet Address</label>
          <input
            id="investorWalletAddress"
            value={identity?.walletAddress || " "}
            readOnly
            className="font-light text-4xl max-[500px]:text-base text-gray-300"
          ></input>
          <label htmlFor="investorIdAccountNumber">Investor KYC ID Provider Account Number</label>
          <input
            id="investorIdAccountNumber"
            value={identity?.accountNumber || " "}
            readOnly
            className="font-light text-4xl max-[500px]:text-base text-gray-300"
          ></input>
        </div>

        {/* Secondary Wallets Section */}
        {SECONDARY_WALLET_ENABLED && (
        <div className="mt-6 mb-6 border p-6 rounded-lg">
          <p className="text-lg font-semibold mb-1">Secondary Wallets</p>
          <p className="text-sm text-gray-500 mb-4">Additional wallets linked to this identity</p>

          {loadingWallets ? (
            <div className="text-center py-8">Loading wallets...</div>
          ) : secondaryWallets.length === 0 ? (
            <div className="border rounded-xl p-6 bg-gray-50 text-center">
              <p className="text-gray-500">No secondary wallets linked to this identity</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {secondaryWallets.map((wallet, index) => (
                <div key={`${wallet.walletAddress || "wallet"}-${index}`} className="border rounded-xl p-4 bg-white">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">Wallet Address:</span>
                    <span className="text-gray-600 font-mono text-sm">{wallet.walletAddress}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {personaData && (
          <div className="flex flex-col gap-2 rounded-lg bg-gray-200 p-6">
            <div className="flex">
              <div className="flex mb-2 mr-5">
                <p className="mr-5">Verification Data</p>
                <span
                  className={`rounded-full border px-4 py-1 text-xs font-bold ${
                    personaData?.name?.split(".")[1]?.toUpperCase() === "APPROVED" || personaData?.name?.split(".")[1]?.toUpperCase() === "COMPLETED"
                      ? "border-green-500 text-green-500"
                      : "border-red-500 text-red-500"
                  }`}
                >
                  {personaData?.name?.split(".")[1]?.toUpperCase() || ""}
                </span>
              </div>
              {identity?.watchlistMatched && (
                <div className="flex mb-2 mr-5">
                  <span className="mr-5">Matched</span>
                  <p className="rounded-full border px-4 py-1 text-xs font-bold border-red-500 text-red-500">Watchlist</p>
                </div>
              )}
              {identity?.pepMatched && (
                <div className="flex mb-2">
                  <span className="mr-5">Matched</span>
                  <p className="rounded-full border px-4 py-1 text-xs font-bold border-red-500 text-red-500">Politically Exposed Person</p>
                </div>
              )}
            </div>
            {verifications && (
              <div className="rounded-lg bg-white p-2">
                <p className="font-bold">Verifications</p>
                <div className="border rounded-xl p-6 bg-white flex flex-col gap-3">
                  {verifications.map((item) => {
                    // Safeguard: Handle undefined type or status
                    const kind = item.kind || "Unknown Verification";
                    const status = item.status || "UNKNOWN";

                    return (
                      <div key={item.id} className="flex flex-col gap-2">
                        <label htmlFor={item.id}>{kind}</label>
                        <input id={item.id} value={status} readOnly className="font-light text-2xl max-[500px]:text-base text-gray-300" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {documents && (
              <div className="rounded-lg bg-white p-2">
                <p className="font-bold">Documents</p>
                <div className="border rounded-xl p-6 bg-white flex flex-col gap-3">
                  {documents.map((item) => (
                    <div key={item.id} className="flex flex-col gap-2">
                      <label htmlFor={item.id}>{item.kind}</label>
                      <input id={item.id} value={item.status || ""} readOnly className="font-light text-2xl max-[500px]:text-base text-gray-300" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        {identityId !== "pending" && identity?.claims?.length > 0 && <p>Active Claims</p>}
        {identity?.claims?.map((item) => {
          return <ClaimCard key={item.id} data={item} />;
        })}
        {identityId !== "pending" && identity?.removedClaims?.length > 0 && <p>Removed Claims</p>}
        {identity?.removedClaims?.map((item) => {
          return <ClaimCard key={item.id} data={item} />;
        })}

        {/* Suspension History — shown only for users with a suspend/unsuspend
            audit entry. */}
        {suspensionHistory.length > 0 && (
          <div className="mt-6 mb-6 border p-6 rounded-lg">
            <p className="text-lg font-semibold mb-1">Suspension History</p>

            <div className="flex flex-col gap-3">{suspensionHistory.map(renderHistoryEntry)}</div>
          </div>
        )}
        {/* Status Change History — shown only for users moved to Denied,
            Under Review, or Tester (the audited transitions). */}
        {otherStatusHistory.length > 0 && (
          <div className="mt-6 mb-6 border p-6 rounded-lg">
            <p className="text-lg font-semibold mb-1">Status Change History</p>

            <div className="flex flex-col gap-3">{otherStatusHistory.map(renderHistoryEntry)}</div>
          </div>
        )}
        {/* Persona Reset History — shown only for users an admin has reset. */}
        {resetHistory.length > 0 && (
          <div className="mt-6 mb-6 border p-6 rounded-lg">
            <p className="text-lg font-semibold mb-1">Persona Reset History</p>

            <div className="flex flex-col gap-3">
              {resetHistory.map((entry) => (
                <div key={entry.objectId} className="border rounded-xl p-4 bg-white">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="rounded-full border px-3 py-1 text-xs font-bold border-[#D97706] text-[#92400E]">Reset</span>
                    {entry.fromStatus && <span className="text-xs text-gray-400">from KYC Status: {toTitleCase(entry.fromStatus)}</span>}
                  </div>
                  <div className="mt-2 text-sm text-gray-700 break-all">
                    <span className="font-semibold">Reset by:</span> {entry.complianceUserEmail || "system"}
                    {entry.complianceUserWallet && <span className="text-gray-500"> ({entry.complianceUserWallet})</span>}
                    {entry.isTrustedIssuer && (
                      <span className="ml-2 rounded-full border px-2 py-0.5 text-xs font-bold border-green-500 text-green-500">Trusted Issuer</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    <span className="font-semibold">On:</span> {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : "—"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {identityId !== "pending" && identity?.activeIdentity && <p>Active Identity</p>}
        {identityId !== "pending" && identity?.activeIdentity && <ActiveIdentityCard data={identity.activeIdentity} />}
        {removedIdentity && <p>Removed Identity</p>}
        {removedIdentity && <RemovedIdentityCard data={removedIdentity} />}
      </div>
      <div className="flex justify-end max-[500px]:justify-center">
        <Button className="max-[500px]:w-[50%] rounded-none my-6 mr-6 h-11 px-10 bg-[#9952b3] text-white" onClick={backBtn}>
          Back
        </Button>
      </div>
    </div>
  );
}

export default DigitalIdentityDetailView;
